import { StyledSelect } from "@ando/ui";
import * as React from "react";
import { Loader2 } from "lucide-react";
import { useQueryClient } from "@tanstack/react-query";

import {
  Button,
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  useToast,
} from "@ando/ui";
import type { AdminOrganization } from "@ando/core";

import {
  adminOrgKeys,
  useUpdateAdminOrg,
} from "@/features/admin/hooks/useAdminOrg";

/**
 * EditOrgDialog — rename + billing email + tier multiplier + max members.
 *
 * Port of v1 ``OrganizationDetailPage`` inline edit dialog (lines 1347-1414).
 * Submits a single PUT ``/platform-admin/organizations/{id}`` via the
 * existing ``useUpdateAdminOrg`` mutation; on success invalidates both the
 * per-org cache and the parent ``["admin","orgs","list"]`` list so the
 * AdminOrgs table reflects the new name/tier immediately.
 *
 * Tier presets mirror those in ``AdminOrgs.tsx`` (the v2 source of truth);
 * if the current ``usage_multiplier`` is outside the presets we surface it
 * as a "Custom (×N)" option so the dropdown remains usable instead of
 * silently snapping to a different value.
 */

interface TierPreset {
  multiplier: number;
  label: string;
}

const TIER_PRESETS: ReadonlyArray<TierPreset> = [
  { multiplier: 1, label: "Standard" },
  { multiplier: 3, label: "Pro" },
  { multiplier: 5, label: "Business" },
  { multiplier: 10, label: "Business+" },
  { multiplier: 20, label: "Enterprise" },
  { multiplier: 50, label: "Enterprise+" },
];

function presetsWithLegacy(current: number): TierPreset[] {
  if (TIER_PRESETS.some((t) => t.multiplier === current)) {
    return [...TIER_PRESETS];
  }
  return [...TIER_PRESETS, { multiplier: current, label: "Custom" }].sort(
    (a, b) => a.multiplier - b.multiplier,
  );
}

export interface EditOrgDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  org: AdminOrganization;
}

interface EditFormState {
  name: string;
  usage_multiplier: number;
  max_members: number;
  billing_email: string;
}

function deriveInitialForm(org: AdminOrganization): EditFormState {
  return {
    name: org.name,
    usage_multiplier: org.usage_multiplier,
    max_members: org.max_members,
    billing_email: org.billing_email ?? "",
  };
}

export function EditOrgDialog({
  open,
  onOpenChange,
  org,
}: EditOrgDialogProps): React.ReactElement {
  const { toast } = useToast();
  const qc = useQueryClient();
  const updateOrg = useUpdateAdminOrg(org.id);

  const [form, setForm] = React.useState<EditFormState>(() =>
    deriveInitialForm(org),
  );

  // Re-seed the form every time the dialog OPENS (not on every render —
  // typing should not reset the value). Closing keeps the dirty state
  // around for one extra tick, which is fine because the dialog content
  // unmounts on close.
  React.useEffect(() => {
    if (open) setForm(deriveInitialForm(org));
  }, [open, org]);

  const handleSubmit = (e: React.FormEvent): void => {
    e.preventDefault();
    if (!form.name.trim()) {
      toast.error("Nome obbligatorio", {
        description: "Inserisci un nome valido per l'organizzazione.",
      });
      return;
    }
    const billingTrimmed = form.billing_email.trim();
    if (billingTrimmed && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(billingTrimmed)) {
      toast.error("Email non valida", {
        description: "Inserisci un'email di fatturazione valida o lasciala vuota.",
      });
      return;
    }
    if (
      !Number.isInteger(form.max_members) ||
      (form.max_members !== -1 && form.max_members < 0)
    ) {
      toast.error("Max membri non valido", {
        description: "Inserisci un intero ≥ 0 (oppure -1 per illimitato).",
      });
      return;
    }
    updateOrg.mutate(
      {
        name: form.name.trim(),
        usage_multiplier: form.usage_multiplier,
        max_members: form.max_members,
        billing_email: billingTrimmed || undefined,
      },
      {
        onSuccess: () => {
          toast.success("Organizzazione aggiornata", {
            description: form.name.trim(),
          });
          // The list surface and the detail header both read from caches:
          // poke both so the rename / tier change is reflected immediately
          // without waiting for staleTime.
          qc.invalidateQueries({ queryKey: adminOrgKeys.list });
          onOpenChange(false);
        },
        onError: (err) => {
          toast.error("Aggiornamento fallito", {
            description:
              err instanceof Error ? err.message : "Errore inatteso.",
          });
        },
      },
    );
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-lg">
        <form onSubmit={handleSubmit} className="space-y-4">
          <DialogHeader>
            <DialogTitle>Modifica organizzazione</DialogTitle>
            <DialogDescription>Stai modificando {org.name}</DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <div className="space-y-1.5">
              <label
                htmlFor="edit-org-name"
                className="block font-semibold text-[10px] text-ink-3 uppercase tracking-wide"
              >
                Nome
              </label>
              <input
                id="edit-org-name"
                value={form.name}
                onChange={(e) =>
                  setForm((f) => ({ ...f, name: e.target.value }))
                }
                className="h-9 w-full rounded-md border border-hairline bg-card px-3 text-[13px] text-ink-1 focus-visible:border-accent focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent/40"
              />
            </div>
            <div className="space-y-1.5">
              <label
                htmlFor="edit-org-tier"
                className="block font-semibold text-[10px] text-ink-3 uppercase tracking-wide"
              >
                Tier usage
              </label>
              <StyledSelect
                id="edit-org-tier"
                value={String(form.usage_multiplier)}
                onChange={(e) =>
                  setForm((f) => ({
                    ...f,
                    usage_multiplier: Number(e.target.value),
                  }))
                }
                className="h-9 w-full rounded-md border border-hairline bg-card px-2 text-[13px] text-ink-1 focus-visible:border-accent focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent/40"
              >
                {presetsWithLegacy(form.usage_multiplier).map((t) => (
                  <option key={t.multiplier} value={String(t.multiplier)}>
                    ×{t.multiplier} · {t.label}
                  </option>
                ))}
              </StyledSelect>
            </div>
            <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
              <div className="space-y-1.5">
                <label
                  htmlFor="edit-org-max-members"
                  className="block font-semibold text-[10px] text-ink-3 uppercase tracking-wide"
                >
                  Max membri
                </label>
                <input
                  id="edit-org-max-members"
                  type="number"
                  min={-1}
                  step={1}
                  value={form.max_members}
                  onChange={(e) =>
                    setForm((f) => ({
                      ...f,
                      max_members: Number(e.target.value),
                    }))
                  }
                  className="h-9 w-full rounded-md border border-hairline bg-card px-3 text-[13px] text-ink-1 tabular-nums focus-visible:border-accent focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent/40"
                />
                <p className="text-[11px] text-ink-3">−1 = illimitato</p>
              </div>
              <div className="space-y-1.5">
                <label
                  htmlFor="edit-org-billing-email"
                  className="block font-semibold text-[10px] text-ink-3 uppercase tracking-wide"
                >
                  Email fatturazione
                </label>
                <input
                  id="edit-org-billing-email"
                  type="email"
                  value={form.billing_email}
                  onChange={(e) =>
                    setForm((f) => ({ ...f, billing_email: e.target.value }))
                  }
                  placeholder="billing@example.com"
                  className="h-9 w-full rounded-md border border-hairline bg-card px-3 text-[13px] text-ink-1 placeholder:text-ink-3 focus-visible:border-accent focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent/40"
                />
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button
              type="button"
              variant="ghost"
              onClick={() => onOpenChange(false)}
              disabled={updateOrg.isPending}
            >
              Annulla
            </Button>
            <Button type="submit" disabled={updateOrg.isPending}>
              {updateOrg.isPending ? (
                <Loader2 size={14} className="mr-2 animate-spin" aria-hidden />
              ) : null}
              Salva
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
